import { ref } from 'vue'
import { websiteApi } from '~/api/website'
import { useDebounceFn } from '@/composables/useDebounceThrottle'
import { normalizeServiceSearchKeyword } from '@/utils/website/serviceContentSearch'

/**
 * @description 获取内容类服务页搜索联想，下载中心 / 帮助中心 / 公告中心按 centerType 复用。
 * @param {Object} options - 联想配置
 * @param {number} options.centerType - 所属中心：1 下载中心，2 帮助中心，3 公告中心
 * @param {number} [options.size=10] - 联想条数
 * @param {number} [options.wait=500] - 防抖等待毫秒
 */
export function useServiceContentSuggestions({ centerType, size = 10, wait = 500 } = {}) {
  const suggestions = ref([])
  const suggestionsLoading = ref(false)
  const suggestionsError = ref(false)
  const requestId = ref(0)

  const fetchSuggestions = async (keyword, filters, currentRequestId) => {
    const searchText = normalizeServiceSearchKeyword(keyword)
    if (!searchText) {
      if (currentRequestId === requestId.value) {
        suggestions.value = []
        suggestionsLoading.value = false
        suggestionsError.value = false
      }
      return
    }

    try {
      const response = await websiteApi.getContentList({
        current: 1,
        size,
        centerType,
        keyword: searchText,
        ...filters,
      })
      if (currentRequestId !== requestId.value) return

      if (!Array.isArray(response?.records)) throw new Error('联想响应格式异常')
      const records = response.records
      suggestions.value = records
        .map((item) => {
          const contentId = String(item?.contentId ?? '').trim()
          const label = String(
            item?.displayName || item?.title || item?.contentTitle || item?.name || ''
          ).trim()
          const value = String(item?.value || label).trim()
          if (!contentId || !label) return null

          return {
            contentId,
            label,
            value,
            centerTypes: item?.centerTypes,
            contentForm: item?.contentForm,
            staticSiteUrl: item?.staticSiteUrl,
          }
        })
        .filter(Boolean)
        .slice(0, Math.min(size, 10))
      suggestionsError.value = false
    } catch {
      if (currentRequestId !== requestId.value) return
      suggestions.value = []
      suggestionsError.value = true
    } finally {
      if (currentRequestId === requestId.value) {
        suggestionsLoading.value = false
      }
    }
  }

  const debouncedFetchSuggestions = useDebounceFn(fetchSuggestions, wait)

  const clearSuggestions = () => {
    debouncedFetchSuggestions.cancel?.()
    requestId.value += 1
    suggestions.value = []
    suggestionsLoading.value = false
    suggestionsError.value = false
  }

  const scheduleSuggestions = (keyword, filters = {}) => {
    debouncedFetchSuggestions.cancel?.()
    const searchText = normalizeServiceSearchKeyword(keyword)
    requestId.value += 1
    const currentRequestId = requestId.value

    if (!searchText || !centerType) {
      suggestions.value = []
      suggestionsLoading.value = false
      suggestionsError.value = false
      return
    }

    suggestions.value = []
    suggestionsLoading.value = true
    suggestionsError.value = false
    debouncedFetchSuggestions(searchText, filters, currentRequestId)
  }

  return {
    suggestions,
    suggestionsLoading,
    suggestionsError,
    scheduleSuggestions,
    clearSuggestions,
  }
}
